// Screen-reader / text-to-speech service (WCAG 2.1 SC 1.1.1).
//
// When active, clicks on text or imagery read the relevant spoken
// alternative aloud via the browser's built-in speechSynthesis API:
//   - <img alt> for images
//   - [aria-label] or [title] for icon buttons and widgets
//   - innerText for plain copy, headings, paragraphs, list items
//
// The click still propagates to its native handler, so the feature
// is additive — navigation, form submission, etc. behave normally.
// Pressing Escape cancels any in-progress utterance.

let active = false;
let onDocumentClick = null;
let onDocumentKey = null;

const READABLE_SELECTOR =
  'a,button,[role="button"],[role="link"],h1,h2,h3,h4,h5,h6,p,li,' +
  'label,summary,article,section,figure,figcaption,blockquote,' +
  'time,dd,dt,address,cite,code,caption,span,div,img';

function supported() {
  return typeof window !== 'undefined' && 'speechSynthesis' in window;
}

// Choose the best spoken text for a given element. Prefer explicit
// accessibility annotations over raw innerText so icon-only buttons
// and images with alt text read as their intended label.
function textFor(el) {
  if (!el || !el.getAttribute) return '';
  const ariaLabel = el.getAttribute('aria-label');
  if (ariaLabel && ariaLabel.trim()) return ariaLabel.trim();

  if (el.tagName === 'IMG') {
    const alt = el.getAttribute('alt');
    if (alt && alt.trim()) return alt.trim();
    return '';
  }

  const title = el.getAttribute('title');
  const text = (el.innerText || el.textContent || '').trim();
  if (text) return text;
  if (title && title.trim()) return title.trim();
  return '';
}

function speak(text) {
  if (!supported() || !text) return;
  const synth = window.speechSynthesis;
  // Cancel any previous utterance so the latest click wins — avoids
  // queuing up a wall of text when users click through quickly.
  synth.cancel();
  const utter = new SpeechSynthesisUtterance(text);
  utter.rate = 1;
  utter.pitch = 1;
  utter.volume = 1;
  utter.lang = document.documentElement.lang || 'en-US';
  synth.speak(utter);
}

function handleClick(event) {
  const target = event.target;
  if (!target || !target.closest) return;

  // Never read the accessibility panel itself — the user is
  // interacting with controls there, not consuming content.
  if (target.closest('.a11y-wrap')) return;

  // Find the nearest readable ancestor; fall back to the target.
  const el = target.closest(READABLE_SELECTOR) || target;
  const text = textFor(el);
  if (text) speak(text);
}

function handleKey(event) {
  // Escape stops any in-progress reading — classic "shut up" gesture.
  if (event.key === 'Escape' && supported()) {
    window.speechSynthesis.cancel();
  }
}

export function startScreenReader() {
  if (active || !supported()) return;
  active = true;
  onDocumentClick = handleClick;
  onDocumentKey = handleKey;
  // Capture phase so we hear the click even when a child handler
  // calls stopPropagation() — native navigation still proceeds.
  document.addEventListener('click', onDocumentClick, true);
  document.addEventListener('keydown', onDocumentKey);
  document.body.classList.add('a11y-screen-reader');
}

export function stopScreenReader() {
  if (!active) return;
  active = false;
  if (supported()) window.speechSynthesis.cancel();
  if (onDocumentClick)
    document.removeEventListener('click', onDocumentClick, true);
  if (onDocumentKey) document.removeEventListener('keydown', onDocumentKey);
  onDocumentClick = null;
  onDocumentKey = null;
  document.body.classList.remove('a11y-screen-reader');
}
